// @ts-check

/**
 * Lasirius Flow Rail Web Component
 * Controls horizontal discovery rail navigation and keyboard controls.
 */
class LasiriusFlowRail extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLElement | null} */
    this.track = null;
    /** @type {HTMLElement[]} */
    this.panels = [];
    /** @type {HTMLButtonElement | null} */
    this.previousButton = null;
    /** @type {HTMLButtonElement | null} */
    this.nextButton = null;
    /** @type {HTMLElement | null} */
    this.position = null;
    /** @type {HTMLElement | null} */
    this.activeName = null;
    /** @type {HTMLElement | null} */
    this.progress = null;
    /** @type {number} */
    this.activeIndex = 0;
    /** @type {number | null} */
    this.frame = null;
    /** @type {MediaQueryList | null} */
    this.motionQuery = null;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-flow-track]');
    this.panels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-flow-panel]')]);
    this.previousButton = this.querySelector('[data-flow-previous]');
    this.nextButton = this.querySelector('[data-flow-next]');
    this.position = this.querySelector('[data-flow-position]');
    this.activeName = this.querySelector('[data-flow-active-name]');
    this.progress = this.querySelector('[data-flow-progress]');

    if (!this.track || this.panels.length === 0) return;

    this.activeIndex = 0;
    this.frame = null;
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.previousButton?.addEventListener('click', () => this.move(-1));
    this.nextButton?.addEventListener('click', () => this.move(1));
    this.track.addEventListener('scroll', () => this.requestUpdate(), { passive: true });
    this.track.addEventListener('keydown', (event) => this.handleKeydown(event));
    window.addEventListener('resize', () => this.requestUpdate(), { passive: true });

    this.update(0);
  }

  disconnectedCallback() {
    if (this.frame !== null) cancelAnimationFrame(this.frame);
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    event.preventDefault();
    if (event.key === 'Home') this.goTo(0);
    if (event.key === 'End') this.goTo(this.panels.length - 1);
    if (event.key === 'ArrowLeft') this.move(-1);
    if (event.key === 'ArrowRight') this.move(1);
  }

  /**
   * @param {number} direction
   */
  move(direction) {
    const target = Math.min(this.panels.length - 1, Math.max(0, this.activeIndex + direction));
    this.goTo(target);
  }

  /**
   * @param {number} index
   */
  goTo(index) {
    const panel = this.panels[index];
    if (!panel) return;

    panel.scrollIntoView({
      behavior: this.motionQuery?.matches ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'start'
    });
    this.update(index);
  }

  requestUpdate() {
    if (this.frame !== null || !this.track) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      if (!this.track) return;
      const trackLeft = this.track.getBoundingClientRect().left;
      let minDistance = Number.POSITIVE_INFINITY;
      let nearestIndex = 0;

      for (let i = 0; i < this.panels.length; i++) {
        const p = this.panels[i];
        if (!p) continue;
        const dist = Math.abs(p.getBoundingClientRect().left - trackLeft);
        if (dist < minDistance) {
          minDistance = dist;
          nearestIndex = i;
        }
      }
      this.update(nearestIndex);
    });
  }

  /**
   * @param {number} index
   */
  update(index) {
    this.activeIndex = index;
    const total = this.panels.length;
    const activePanel = this.panels[index];

    for (let i = 0; i < this.panels.length; i++) {
      const panel = this.panels[i];
      if (!panel) continue;
      const active = i === index;
      panel.toggleAttribute('data-active', active);
      panel.setAttribute('aria-current', active ? 'true' : 'false');
    }

    if (this.position) this.position.textContent = `${index + 1} / ${total}`;
    if (this.activeName && activePanel) this.activeName.textContent = activePanel.dataset.flowName || '';
    if (this.progress) this.progress.style.setProperty('--flow-progress', `${((index + 1) / total) * 100}%`);
    if (this.previousButton) this.previousButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === total - 1;
  }
}

if (!customElements.get('lasirius-flow-rail')) {
  customElements.define('lasirius-flow-rail', LasiriusFlowRail);
}

/**
 * Lasirius Product Slider Web Component
 * High-performance single-row product slider with swipe, navigation arrows & button states.
 */
class LasiriusProductSlider extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLElement | null} */
    this.track = null;
    /** @type {HTMLButtonElement | null} */
    this.prevBtn = null;
    /** @type {HTMLButtonElement | null} */
    this.nextBtn = null;
    /** @type {() => void} */
    this.onPrev = () => {};
    /** @type {() => void} */
    this.onNext = () => {};
    /** @type {() => void} */
    this.updateButtons = () => {};
  }

  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    this.prevBtn = this.querySelector('[data-slider-prev]');
    this.nextBtn = this.querySelector('[data-slider-next]');

    if (!this.track) return;

    this.onPrev = () => {
      if (!this.track) return;
      const scrollAmount = Math.max(280, this.track.clientWidth * 0.75);
      this.track.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    };

    this.onNext = () => {
      if (!this.track) return;
      const scrollAmount = Math.max(280, this.track.clientWidth * 0.75);
      this.track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    };

    this.updateButtons = () => {
      if (!this.track) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      if (this.prevBtn) this.prevBtn.disabled = scrollLeft <= 4;
      if (this.nextBtn) this.nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 6;
    };

    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.updateButtons, { passive: true });
    window.addEventListener('resize', this.updateButtons, { passive: true });

    setTimeout(() => this.updateButtons(), 120);
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this.onPrev);
    this.nextBtn?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.updateButtons);
    window.removeEventListener('resize', this.updateButtons);
  }
}

if (!customElements.get('lasirius-product-slider')) {
  customElements.define('lasirius-product-slider', LasiriusProductSlider);
}
